import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/20/solid';
import PropTypes from 'prop-types';
import { useCallback, useState } from 'react';

const DATE_RANGE_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
];

const EVENT_TYPE_OPTIONS = [
  { value: '', label: 'All Events' },
  { value: 'created,added', label: 'Created / Added' },
  { value: 'updated', label: 'Updated' },
  { value: 'deleted,removed', label: 'Deleted / Removed' },
];

function ActivityFilter({ filters, onFilterChange }) {
  const [searchInput, setSearchInput] = useState(filters.search || '');

  const handleDateRangeChange = useCallback(
    (e) => {
      const { value } = e.target;
      let dateFrom = null;

      if (value) {
        const days = Number.parseInt(value, 10);
        const date = new Date();
        date.setDate(date.getDate() - days);
        dateFrom = date.toISOString();
      }

      onFilterChange({
        ...filters,
        dateFrom,
        dateRange: value,
      });
    },
    [filters, onFilterChange],
  );

  const handleEventTypeChange = useCallback(
    (e) => {
      const { value } = e.target;
      onFilterChange({
        ...filters,
        eventTypes: value ? value.split(',') : null,
      });
    },
    [filters, onFilterChange],
  );

  const handleSearchSubmit = useCallback(
    (e) => {
      e.preventDefault();
      onFilterChange({
        ...filters,
        search: searchInput || null,
      });
    },
    [filters, onFilterChange, searchInput],
  );

  const handleSearchKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        handleSearchSubmit(e);
      }
    },
    [handleSearchSubmit],
  );

  const handleClearFilters = useCallback(() => {
    setSearchInput('');
    onFilterChange({});
  }, [onFilterChange]);

  const hasActiveFilters =
    filters.dateFrom || filters.eventTypes?.length > 0 || filters.search;

  return (
    <div className="mb-4 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        {/* Date Range */}
        <div className="min-w-[140px]">
          <label
            htmlFor="activity-date-range"
            className="block font-medium text-gray-700 text-xs"
          >
            Date Range
          </label>
          <select
            id="activity-date-range"
            value={filters.dateRange || ''}
            onChange={handleDateRangeChange}
            className="mt-1 block w-full rounded-md border-gray-300 py-1.5 pr-10 pl-3 text-sm focus:border-rcprimary-500 focus:outline-none focus:ring-rcprimary-500"
          >
            {DATE_RANGE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Event Type */}
        <div className="min-w-[160px]">
          <label
            htmlFor="activity-event-type"
            className="block font-medium text-gray-700 text-xs"
          >
            Event Type
          </label>
          <select
            id="activity-event-type"
            value={filters.eventTypes?.join(',') || ''}
            onChange={handleEventTypeChange}
            className="mt-1 block w-full rounded-md border-gray-300 py-1.5 pr-10 pl-3 text-sm focus:border-rcprimary-500 focus:outline-none focus:ring-rcprimary-500"
          >
            {EVENT_TYPE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Search */}
        <div className="min-w-[200px] flex-1">
          <label
            htmlFor="activity-search"
            className="block font-medium text-gray-700 text-xs"
          >
            Search
          </label>
          <div className="relative mt-1">
            <input
              type="text"
              id="activity-search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search activity..."
              className="block w-full rounded-md border-gray-300 py-1.5 pr-10 pl-3 text-sm placeholder:text-gray-400 focus:border-rcprimary-500 focus:outline-none focus:ring-rcprimary-500"
            />
            <button
              type="button"
              onClick={handleSearchSubmit}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            >
              <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={handleClearFilters}
            className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2.5 py-1.5 font-medium text-gray-700 text-xs hover:bg-gray-200"
          >
            <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

ActivityFilter.propTypes = {
  filters: PropTypes.shape({
    dateFrom: PropTypes.string,
    dateTo: PropTypes.string,
    dateRange: PropTypes.string,
    eventTypes: PropTypes.arrayOf(PropTypes.string),
    search: PropTypes.string,
  }).isRequired,
  onFilterChange: PropTypes.func.isRequired,
};

export default ActivityFilter;
